import { useMemo } from 'react'

import { IResourceRecord } from '~/client/src/shared/components/ResourceCardList/types'
import { getResourceDefinition } from '~/client/src/shared/resources/definitions'
import { resolveResourceValue } from '~/client/src/shared/resources/resolveResourceValue'

export interface IResourceMapMarker {
  id: string
  latitude: number
  longitude: number
  name?: string
}

// Rejects (0, 0) — default/unset coordinates that map to Null Island
const isValidCoordinate = (latitude: number, longitude: number): boolean =>
  Number.isFinite(latitude) &&
  Number.isFinite(longitude) &&
  latitude >= -90 &&
  latitude <= 90 &&
  longitude >= -180 &&
  longitude <= 180 &&
  (latitude !== 0 || longitude !== 0)

export { isValidCoordinate }

export function useResourceMapMarkers(
  records: IResourceRecord[],
  resourceTypeId: string,
): IResourceMapMarker[] {
  const definition = getResourceDefinition(resourceTypeId)
  const latitudeKey = definition?.map?.latitudeKey
  const longitudeKey = definition?.map?.longitudeKey

  return useMemo(() => {
    if (!latitudeKey || !longitudeKey) {
      return []
    }

    return records
      .map(record => {
        const latitude = Number(
          resolveResourceValue({
            record,
            resourceTypeId,
            key: latitudeKey,
          }),
        )
        const longitude = Number(
          resolveResourceValue({
            record,
            resourceTypeId,
            key: longitudeKey,
          }),
        )

        if (!isValidCoordinate(latitude, longitude)) {
          return null
        }

        return {
          id: record.id,
          latitude,
          longitude,
          name: record.name,
        }
      })
      .filter(Boolean) as IResourceMapMarker[]
  }, [records, resourceTypeId, latitudeKey, longitudeKey])
}
